import { useMutation } from '@tanstack/react-query';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { useSnackbar } from 'notistack';
import { ChangeUserStatusPayload } from '../../../types/user';
import { ChangeOperatorStatusPayload } from '../../../types/operator';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const UserStateToggleDialog = ({
  user,
  onClose,
  onConfirm,
}: {
  user: ChangeUserStatusPayload | null;
  onClose: () => void;
  onConfirm: (updatedUser: ChangeOperatorStatusPayload) => void;
}) => {
  const { updateUserState } = useIotsApi();
  const updateUserStateMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateUserState(id, isActive),
  });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  return (
    <ConfirmationDialog
      open={Boolean(user)}
      onClose={onClose}
      loading={updateUserStateMutation.isPending}
      onConfirm={() => {
        if (!user) {
          return;
        }
        const { id } = user;
        const newIsActive = !user.isActive;
        updateUserStateMutation.mutate(
          { id, isActive: newIsActive },
          {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.user-update-success'),
              });
              onConfirm({ ...user, isActive: newIsActive });
              onClose();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(error, t('content.user-update-error')),
              });
              updateUserStateMutation.reset();
            },
          },
        );
      }}
    >
      {user &&
        t(
          `content.${
            user.isActive
              ? 'user-deactivation-confirmation-request'
              : 'user-activation-confirmation-request'
          }`,
        )}
    </ConfirmationDialog>
  );
};

export default UserStateToggleDialog;
